import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Video, ExternalLink, Sparkles, Loader2, FileSpreadsheet, ChevronDown, ChevronRight, Flame, Target, Users, Tag, Mic, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ViralScoreRadar from '../components/signature/ViralScoreRadar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUserVideos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [analyzingIds, setAnalyzingIds] = useState({});
  const [expandedIds, setExpandedIds] = useState({});

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'operator') { navigate('/create'); return; }
    fetchVideos();
  }, [user, navigate, platformFilter]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const params = {};
      if (platformFilter !== 'all') params.platform = platformFilter;
      const res = await axios.get(`${API_URL}/api/user-videos/admin/list`, { params });
      setVideos(res.data);
    } catch { toast.error('獲取失敗'); }
    finally { setLoading(false); }
  };

  const analyzeVideo = async (videoId) => {
    setAnalyzingIds(p => ({ ...p, [videoId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/user-videos/admin/${videoId}/analyze`);
      setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ai_analysis: res.data.analysis, transcript: res.data.transcript } : v));
      toast.success('AI 分析完成');
    } catch (e) { toast.error(e.response?.data?.detail || '分析失敗'); }
    finally { setAnalyzingIds(p => ({ ...p, [videoId]: false })); }
  };

  const analyzeAll = async () => {
    const unanalyzed = videos.filter(v => !v.ai_analysis);
    if (!unanalyzed.length) { toast.info('所有影片已分析完成'); return; }
    toast.info(`開始批量分析 ${unanalyzed.length} 個影片...`);
    for (const v of unanalyzed) { await analyzeVideo(v.id); }
    toast.success('批量分析完成');
  };

  const downloadExcel = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/user-videos/admin/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `user_videos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Excel 已下載');
    } catch { toast.error('下載失敗'); }
  };

  const toggle = (id) => setExpandedIds(p => ({ ...p, [id]: !p[id] }));
  const platformLabels = { douyin: '抖音', instagram: 'Instagram', tiktok: 'TikTok', xiaohongshu: '小紅書' };
  const analyzedCount = videos.filter(v => v.ai_analysis).length;

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-4 md:py-6 flex-shrink-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg md:text-2xl font-heading font-bold text-neutral-900">用戶內容記錄</h1>
            <p className="text-xs text-neutral-500 mt-0.5">
              共 {videos.length} 條記錄，{analyzedCount} 條已 AI 分析
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-28 h-9 text-xs rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all" className="text-xs">全部平台</SelectItem>
                <SelectItem value="douyin" className="text-xs">抖音</SelectItem>
                <SelectItem value="instagram" className="text-xs">Instagram</SelectItem>
                <SelectItem value="tiktok" className="text-xs">TikTok</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={analyzeAll} size="sm" variant="outline" className="rounded-xl text-xs h-9">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />批量分析
            </Button>
            <Button onClick={downloadExcel} size="sm" className="rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs h-9">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />下載 Excel
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-3">
          {loading ? (
            <div className="text-center py-16 text-neutral-400 text-sm">載入中...</div>
          ) : videos.length === 0 ? (
            <div className="text-center py-16 text-neutral-400">
              <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">暫無影片記錄</p>
            </div>
          ) : videos.map(v => <VideoCard key={v.id} v={v} expanded={!!expandedIds[v.id]} onToggle={() => toggle(v.id)}
            analyzing={!!analyzingIds[v.id]} onAnalyze={() => analyzeVideo(v.id)} platformLabels={platformLabels} />)}
        </div>
      </main>
    </>
  );
};

const VideoCard = ({ v, expanded, onToggle, analyzing, onAnalyze, platformLabels }) => {
  const a = v.ai_analysis || {};
  const has = !!v.ai_analysis;

  return (
    <Card className={`overflow-hidden transition-all ${has ? 'hover-glow' : 'border-dashed'}`} data-testid={`video-card-${v.id}`}>
      {/* Compact header - always visible */}
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <button className="flex-shrink-0 text-neutral-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
          v.platform === 'instagram' ? 'bg-pink-100 text-pink-700' :
          v.platform === 'douyin' ? 'bg-cyan-100 text-cyan-700' :
          v.platform === 'xiaohongshu' ? 'bg-red-100 text-red-700' :
          'bg-neutral-100 text-neutral-600'
        }`}>
          {platformLabels[v.platform] || v.platform}
        </span>

        {has ? (
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-neutral-900 truncate block">{a.topic}</span>
            <div className="flex items-center gap-2 mt-0.5">
              {a.industry && <span className="text-[10px] text-neutral-500">{a.industry}</span>}
              {a.viral_potential && (
                <span className={`text-[10px] font-bold flex items-center gap-0.5 ${
                  a.viral_potential >= 8 ? 'text-red-500' : a.viral_potential >= 5 ? 'text-amber-500' : 'text-neutral-400'
                }`}>
                  <Flame className="w-3 h-3" />{a.viral_potential}/10
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <span className="text-xs text-neutral-400 truncate block">{v.video_url}</span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] text-neutral-400 hidden md:block">{v.user_email || v.user_name}</span>
          <span className="text-[10px] text-neutral-300">{v.created_at?.slice(0, 10)}</span>
          {v.video_url && (
            <a href={v.video_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              className="p-1 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600"><ExternalLink className="w-3.5 h-3.5" /></a>
          )}
          <Button onClick={e => { e.stopPropagation(); onAnalyze(); }} size="sm" variant={has ? 'ghost' : 'outline'}
            disabled={analyzing} className="rounded-lg text-[10px] h-7 px-2">
            {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            <span className="ml-1">{has ? '重新' : '分析'}</span>
          </Button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && has && (
        <div className="px-4 pb-4 border-t border-neutral-100 pt-4 space-y-4">
          {/* Viral Score Radar — signature visualization */}
          <ViralScoreRadar analysis={a} size={240} />

          {/* Analysis Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <InfoCell icon={Tag} label="行業" value={a.industry} color="blue" />
            <InfoCell icon={Target} label="內容類型" value={a.content_type} color="purple" />
            <InfoCell icon={Users} label="目標受眾" value={a.target_audience} color="green" />
            <InfoCell icon={Mic} label="情感基調" value={a.emotional_tone} color="amber" />
          </div>

          {/* Hook - prominent display with original text + analysis */}
          {(a.hook_text || a.hook) && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200/50 space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-bold text-amber-700">Hook（開場鉤子）</span>
              </div>
              {/* Original hook text from transcript */}
              {a.hook_text && (
                <div className="bg-white/70 rounded-lg p-3 border border-amber-200/30">
                  <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">原文</span>
                  <p className="text-sm text-amber-950 leading-relaxed italic">「{a.hook_text}」</p>
                </div>
              )}
              {/* Hook technique analysis */}
              {(a.hook_analysis || (!a.hook_text && a.hook)) && (
                <div>
                  <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">手法分析</span>
                  <p className="text-xs text-amber-800 leading-relaxed">{a.hook_analysis || a.hook}</p>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          {a.summary && (
            <div className="bg-neutral-50 rounded-xl p-4">
              <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">摘要</span>
              <p className="text-sm text-neutral-700 mt-1 leading-relaxed">{a.summary}</p>
            </div>
          )}

          {/* Key Points */}
          {a.key_points && a.key_points.length > 0 && (
            <div>
              <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2 block">內容要點</span>
              <div className="flex flex-wrap gap-1.5">
                {a.key_points.map((p, i) => (
                  <span key={i} className="text-xs bg-primary-50 text-primary-700 px-2.5 py-1 rounded-lg">{p}</span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          {a.cta && (
            <div className="text-xs text-neutral-500"><span className="font-medium text-neutral-700">CTA：</span>{a.cta}</div>
          )}

          {/* Transcript */}
          {v.transcript && (
            <details className="group">
              <summary className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-600 flex items-center gap-1">
                文字稿
                <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="mt-2 text-xs text-neutral-500 leading-relaxed bg-neutral-50 rounded-xl p-4 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {v.transcript}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Expanded but no analysis */}
      {expanded && !has && (
        <div className="px-4 pb-4 border-t border-neutral-100 pt-4 text-center">
          <p className="text-xs text-neutral-400 mb-2">尚未進行 AI 分析</p>
          <p className="text-[10px] text-neutral-300">點擊「分析」按鈕，AI 將自動拆解影片的主題、行業、受眾、Hook 等資訊</p>
        </div>
      )}
    </Card>
  );
};

const InfoCell = ({ icon: Icon, label, value, color }) => {
  if (!value) return null;
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
  };
  return (
    <div className={`rounded-xl p-3 border ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-[10px] font-bold opacity-70">{label}</span>
      </div>
      <p className="text-xs font-medium">{value}</p>
    </div>
  );
};

export default AdminUserVideos;
